import { actions, assign, createMachine, send } from "xstate"
import * as O from "fp-ts/Option"
import { PriceResponseV2 } from "@ssc-booking/api-contracts"
import { assertEvent } from "../../../../_common/xstate-helpers"
import { suiteCategoryPriceByCodes } from "../../read-models/suite-prices"
import { Receipt, ReceiptStateMachine, ReceiptTypes, SuitePriceTotal } from "../receipt-machine"

const initialContext = { farePrices: {}, suitePrices: [] }

export const receiptMachineV3 = (): ReceiptStateMachine => {
    type Context = ReceiptTypes["Context"]
    type Events = ReceiptTypes["Events"]
    type TypeState = ReceiptTypes["TypeState"]

    return createMachine<Context, Events, TypeState>(
        {
            id: "receipt",
            initial: "empty",
            predictableActionArguments: true,
            context: initialContext,
            states: {
                empty: {
                    on: {
                        FARE_RECEIPT_AVAILABLE: "present.priceFrom",
                    },
                },
                loading: {
                    on: {
                        // FARE_RECEIPT_AVAILABLE: "#receipt.present.priceFrom",
                        // SUITE_CATEGORY_RECEIPT_AVAILABLE: "#receipt.present.priceTotal",
                    },
                },
                present: {
                    states: {
                        priceFrom: {
                            entry: ["resetSelectedSuiteCategory"],
                            on: {
                                FARE_PRICE_LOADING: { target: "#receipt.loading", cond: "isLoadingSelectedFare" },
                            },
                        },
                        priceTotal: {
                            on: {
                                CHECKOUT_VALIDATED: { actions: ["updateSuitePaymentReceipt"] },
                                CHECKOUT_STARTED: { actions: ["updateSuitePaymentReceipt"] },

                                FARE_SELECTED: {
                                    target: "priceFrom",
                                    actions: ["setSelectedFare", "updateFareReceipt"],
                                },
                                SUITE_PRICES_LOADING: "#receipt.loading",
                                SUITE_CATEGORY_UNSELECTED: {
                                    target: "priceFrom",
                                    actions: ["resetSelectedSuiteCategory", "updateFareReceipt"],
                                },
                            },
                        },
                    },
                },
            },
            on: {
                FARE_RECEIPT_AVAILABLE: "#receipt.present.priceFrom",
                SUITE_CATEGORY_RECEIPT_AVAILABLE: "#receipt.present.priceTotal",
                GUESTS_CHANGED: { target: "#receipt.empty", actions: ["resetInitialContext"] },
                FARE_UNSELECTED: { target: "#receipt.empty", actions: ["resetSelectedFare"] },

                FARE_PRICE_LOADED: { actions: ["setFarePrices", "updateFareReceipt"] },
                FARE_SELECTED: { actions: ["setSelectedFare", "updateFareReceipt"] },

                SUITE_PRICES_LOADED: { actions: ["setSuitePrices", "updateSuiteCategoryReceipt"] },
                SUITE_CATEGORY_SELECTED: {
                    actions: ["setSelectedSuiteCategory", "updateSuiteCategoryReceipt"],
                },
                QUOTE_SUITE_CATEGORY_SELECTED: {
                    actions: ["setSelectedSuiteCategory", "updateSuiteCategoryReceipt"],
                },
            },
        },
        {
            guards: {
                isLoadingSelectedFare: (ctx, ev) => {
                    assertEvent(ev, "FARE_PRICE_LOADING")
                    return ev.index == ctx.selectedFare?.index
                },
            },
            actions: {
                resetInitialContext: assign<Context, Events>(() => ({
                    ...initialContext,
                    selectedFare: undefined,
                    selectedSuiteCategory: undefined,
                    receipt: undefined,
                })),

                setSelectedFare: assign<Context, Events>({
                    selectedFare: (_ctx, ev) => {
                        assertEvent(ev, "FARE_SELECTED")
                        return ev.selectedFare
                    },
                    selectedSuiteCategory: undefined,
                }),
                setSelectedSuiteCategory: assign<Context, Events>({
                    selectedSuiteCategory: (_ctx, ev) => {
                        assertEvent(ev, "SUITE_CATEGORY_SELECTED", "QUOTE_SUITE_CATEGORY_SELECTED")
                        return ev.suiteCategory
                    },
                }),
                setFarePrices: assign<Context, Events>({
                    farePrices: (ctx, ev) => {
                        assertEvent(ev, "FARE_PRICE_LOADED")
                        return {
                            ...ctx.farePrices,
                            [ev.index]: ev.response,
                        }
                    },
                }),
                setSuitePrices: assign<Context, Events>({
                    suitePrices: (_ctx, ev) => {
                        assertEvent(ev, "SUITE_PRICES_LOADED")
                        return ev.prices
                    },
                }),

                updateFareReceipt: actions.pure<Context, Events>((ctx) => {
                    const selectedFare = ctx.selectedFare
                    const selectedSuiteCategory = ctx.selectedSuiteCategory

                    if (!selectedFare || selectedSuiteCategory) return []
                    const farePrice = ctx.farePrices[selectedFare.index]
                    if (!farePrice) return []

                    return [
                        assign<Context, Events>({
                            receipt: { price: farePrice.price, type: "price-from" },
                        }),
                        send<Context, Events, Events>({ type: "FARE_RECEIPT_AVAILABLE" }),
                    ]
                }),

                updateSuiteCategoryReceipt: actions.pure<Context, Events>((ctx) => {
                    const selectedSuiteCategory = ctx.selectedSuiteCategory
                    if (!selectedSuiteCategory) return []
                    const suitePrice = suiteCategoryPriceByCodes(ctx.suitePrices, [selectedSuiteCategory])
                    if (O.isNone(suitePrice)) return []

                    return [
                        assign<Context, Events>({
                            receipt: () => toSuitePriceTotal(suitePrice.value),
                        }),
                        send<Context, Events, Events>({ type: "SUITE_CATEGORY_RECEIPT_AVAILABLE" }),
                    ]
                }),

                updateSuitePaymentReceipt: actions.pure<Context, Events>((ctx) => {
                    const receipt: Receipt | undefined = ctx.receipt
                    if (!receipt || receipt.type === "price-from") return []

                    return [
                        assign<Context, Events>({
                            receipt: (_, ev) => {
                                assertEvent(ev, "CHECKOUT_VALIDATED", "CHECKOUT_STARTED")
                                return {
                                    ...receipt,
                                    suitePayment: ev.response.validatedPrice.suitePayment,
                                }
                            },
                        }),
                    ]
                }),

                resetSelectedFare: assign<Context, Events>({
                    selectedFare: undefined,
                    selectedSuiteCategory: undefined,
                    receipt: undefined,
                    suitePrices: [],
                }),

                resetSelectedSuiteCategory: assign<Context, Events>({
                    selectedSuiteCategory: undefined,
                }),
                logMe: actions.log<Context, Events>((ctx, ev) => ({ type: ev.type, ctx, ev })),
            },
        },
    )
}

const toSuitePriceTotal = (x: PriceResponseV2): SuitePriceTotal => ({
    price: x.price,
    type: "price-total",
    suitePayment: x.suitePayment,
})
